import type { SeedIsland } from './seedLatin'

/**
 * The expansion set for French, Spanish and Italian — the same eight topics the
 * German set carries, so someone learning two of them is drilling comparable
 * material rather than two different curricula.
 *
 * Notes flag what an English speaker actually gets wrong: which verbs put the
 * person in the indirect object, where the subjunctive is obligatory rather
 * than decorative, and the handful of phrases that are idiom rather than
 * grammar.
 */
export const MORE_ISLANDS_ROMANCE: Record<string, SeedIsland[]> = {
  fr: [
    {
      slug: 'habiter',
      title: 'Where I live',
      nativeTitle: "Où j'habite",
      useWhen: 'Pairs with the work island — somebody always asks where you came from.',
      tags: ['introductions', 'place'],
      level: 2,
      blocks: [
        ["J'habite dans une petite ville du Midwest.", 'I live in a small city in the Midwest.'],
        ["Il n'y a pas grand-chose, mais c'est justement ce qui me plaît.", "There isn't much there, but that's exactly what I like about it."],
        ['Les hivers sont longs et assez froids.', 'The winters are long and quite cold.'],
        ["Il faut un quart d'heure en voiture pour aller au centre.", "It's a quarter of an hour by car to the centre.", 'il faut + time is how French states duration impersonally.'],
        ["Je m'y plais bien, mais la mer me manque.", 'I like it there, but I miss the sea.', 'manquer inverts: la mer *me* manque — the sea is missing *to me*.'],
      ],
    },
    {
      slug: 'loisirs',
      title: 'What I do outside work',
      nativeTitle: 'Mes loisirs',
      useWhen: 'The island that keeps a conversation going once the basics are done.',
      tags: ['introductions', 'hobbies'],
      level: 2,
      blocks: [
        ['Pendant mon temps libre, je lis beaucoup et je cours.', 'In my free time I read a lot and I run.'],
        ['Le week-end, je fais en général une dizaine de kilomètres.', 'At the weekend I usually do about ten kilometres.', 'une dizaine — French prefers the approximate noun to "about ten".'],
        ["À part ça, je m'intéresse beaucoup à la cuisine.", "Apart from that, I'm very interested in cooking."],
        ["En ce moment, j'apprends à faire des plats français.", "At the moment I'm learning to cook French dishes."],
        ["Pour l'instant, rien ne me réussit vraiment.", 'So far nothing really turns out well.'],
      ],
    },
    {
      slug: 'deplacements',
      title: 'Getting around',
      nativeTitle: 'Se déplacer',
      useWhen: 'You need this while tired and carrying luggage. Overlearn it.',
      tags: ['transactional', 'travel'],
      level: 2,
      blocks: [
        ['Pardon, pour aller à la gare, s’il vous plaît ?', 'Excuse me, how do I get to the station?', 'pour aller à… is the fixed way to ask directions; a full sentence sounds bookish.'],
        ['Est-ce que ce bus va à l’aéroport ?', 'Does this bus go to the airport?'],
        ['Un aller-retour pour Lyon, s’il vous plaît.', 'A return to Lyon, please.'],
        ['Le train part de quel quai ?', 'Which platform does the train leave from?'],
        ['Vous pourriez me dire quand descendre ?', 'Could you tell me when to get off?'],
      ],
    },
    {
      slug: 'courses',
      title: 'Shopping',
      nativeTitle: 'Faire des courses',
      useWhen: 'Short, frequent exchanges where hesitating costs you the queue.',
      tags: ['transactional', 'shopping'],
      level: 1,
      blocks: [
        ['Pardon, vous l’avez dans une autre taille ?', 'Excuse me, do you have it in another size?'],
        ['Je regarde, merci.', "I'm just looking, thanks.", 'The stock answer to Je peux vous aider ? Have it ready.'],
        ['Ça coûte combien ?', 'How much is it?'],
        ['Je peux payer par carte ?', 'Can I pay by card?'],
        ['Je vais prendre ça, merci.', "I'll take this, thanks."],
      ],
    },
    {
      slug: 'bavardage',
      title: 'Weather and small talk',
      nativeTitle: 'Bavardage',
      useWhen: 'The filler that keeps a silence from becoming awkward.',
      tags: ['social'],
      level: 1,
      blocks: [
        ['Il fait beau aujourd’hui, non ?', "Lovely weather today, isn't it?", 'Weather takes il fait, never il est.'],
        ['Ça se réchauffe enfin.', "It's finally warming up."],
        ['On n’avait pas eu un temps pareil depuis longtemps.', "We hadn't had weather like this in a long time."],
        ['Quelle journée…', 'What a day…'],
        ['Ce week-end tombe vraiment à pic.', 'This weekend comes at just the right moment.', 'tomber à pic — to come at exactly the right time.'],
      ],
    },
    {
      slug: 'rendezvous',
      title: 'Making plans',
      nativeTitle: 'Prendre rendez-vous',
      useWhen: 'The moment a conversation could become a second meeting.',
      tags: ['social'],
      level: 2,
      blocks: [
        ['Ça te dirait de prendre un café un de ces jours ?', 'Would you fancy getting a coffee one of these days?', 'Ça te dirait de… is the natural, unpushy invitation.'],
        ['Quand est-ce que ça t’arrangerait ?', 'When would suit you?'],
        ['En semaine, je suis assez libre.', "During the week I'm fairly free."],
        ['On dit jeudi, vers sept heures ?', 'Shall we say Thursday, around seven?'],
        ['Je t’envoie l’adresse par message.', "I'll send you the address by text."],
      ],
    },
    {
      slug: 'problemes',
      title: 'When something goes wrong',
      nativeTitle: 'Quand ça se passe mal',
      useWhen: 'The island you will need under stress, which is when improvising fails.',
      tags: ['transactional', 'problems'],
      level: 2,
      blocks: [
        ['Excusez-moi, je crois qu’il y a un problème.', 'Excuse me, I think there is a problem.'],
        ['J’ai commandé ça hier, mais je n’ai rien reçu.', "I ordered this yesterday, but I haven't received anything."],
        ['Vous pourriez m’aider, s’il vous plaît ?', 'Could you help me, please?'],
        ['Je n’ai pas bien compris — vous pourriez répéter ?', "I didn't quite understand — could you say that again?"],
        ['Vous pouvez parler un peu plus lentement, s’il vous plaît ?', 'Could you speak a little more slowly, please?'],
      ],
    },
    {
      slug: 'avis',
      title: 'Opinions and preferences',
      nativeTitle: 'Mon avis',
      useWhen: 'What turns an interview into a conversation.',
      tags: ['social', 'opinions'],
      level: 3,
      blocks: [
        ['Je trouve que c’est une bonne idée.', "I think it's a good idea.", 'trouver que + indicative for a positive opinion.'],
        ['Je ne suis pas convaincu, franchement.', "I'm not convinced, frankly."],
        ['Honnêtement, ça ne me plaît pas beaucoup.', "Honestly, I don't much like it."],
        ['Ça dépend.', 'It depends.'],
        ['Je suis assez d’accord avec toi.', 'I fairly much agree with you.'],
      ],
    },
  ],

  es: [
    {
      slug: 'vivir',
      title: 'Where I live',
      nativeTitle: 'Dónde vivo',
      useWhen: 'Pairs with the work island — somebody always asks where you came from.',
      tags: ['introductions', 'place'],
      level: 2,
      blocks: [
        ['Vivo en una ciudad pequeña del Medio Oeste.', 'I live in a small city in the Midwest.'],
        ['No hay mucho que hacer, pero eso es justo lo que me gusta.', "There isn't much to do, but that's exactly what I like."],
        ['Los inviernos son largos y bastante fríos.', 'The winters are long and quite cold.'],
        ['Del centro me separan unos quince minutos en coche.', "I'm about fifteen minutes from the centre by car."],
        ['Estoy a gusto allí, pero echo de menos el mar.', "I'm comfortable there, but I miss the sea.", 'echar de menos is the everyday "to miss"; extrañar is more common in Latin America.'],
      ],
    },
    {
      slug: 'aficiones',
      title: 'What I do outside work',
      nativeTitle: 'Mis aficiones',
      useWhen: 'The island that keeps a conversation going once the basics are done.',
      tags: ['introductions', 'hobbies'],
      level: 2,
      blocks: [
        ['En mi tiempo libre leo mucho y salgo a correr.', 'In my free time I read a lot and go running.'],
        ['Los fines de semana suelo hacer unos diez kilómetros.', 'At weekends I usually do about ten kilometres.', 'soler + infinitive is the neat way to say "usually".'],
        ['Además, me interesa mucho la cocina.', "Besides that, I'm very interested in cooking."],
        ['Últimamente estoy aprendiendo a hacer platos españoles.', "Lately I've been learning to cook Spanish dishes."],
        ['De momento no me sale nada especialmente bien.', 'So far nothing turns out especially well.', 'salir here means "to turn out" — no me sale, it isn’t working for me.'],
      ],
    },
    {
      slug: 'moverse',
      title: 'Getting around',
      nativeTitle: 'Moverse por la ciudad',
      useWhen: 'You need this while tired and carrying luggage. Overlearn it.',
      tags: ['transactional', 'travel'],
      level: 2,
      blocks: [
        ['Perdone, ¿para ir a la estación?', 'Excuse me, how do I get to the station?', '¿Para ir a…? is the compact way to ask directions.'],
        ['¿Este autobús va al aeropuerto?', 'Does this bus go to the airport?'],
        ['Un billete de ida y vuelta a Sevilla, por favor.', 'A return ticket to Seville, please.'],
        ['¿De qué andén sale el tren?', 'Which platform does the train leave from?'],
        ['¿Me avisa cuando tenga que bajarme?', 'Would you tell me when I have to get off?', 'cuando + subjunctive for something that has not happened yet.'],
      ],
    },
    {
      slug: 'compras',
      title: 'Shopping',
      nativeTitle: 'De compras',
      useWhen: 'Short, frequent exchanges where hesitating costs you the queue.',
      tags: ['transactional', 'shopping'],
      level: 1,
      blocks: [
        ['Perdone, ¿lo tienen en otra talla?', 'Excuse me, do you have it in another size?'],
        ['Solo estoy mirando, gracias.', "I'm just looking, thanks."],
        ['¿Cuánto cuesta?', 'How much is it?'],
        ['¿Puedo pagar con tarjeta?', 'Can I pay by card?'],
        ['Me llevo esto, gracias.', "I'll take this, thanks.", 'llevarse is what you say at the till, not tomar.'],
      ],
    },
    {
      slug: 'charla',
      title: 'Weather and small talk',
      nativeTitle: 'Charla',
      useWhen: 'The filler that keeps a silence from becoming awkward.',
      tags: ['social'],
      level: 1,
      blocks: [
        ['Hace muy buen día, ¿no?', "It's a lovely day, isn't it?", 'Weather takes hacer: hace frío, hace sol, hace buen día.'],
        ['Por fin está empezando a hacer calor.', "It's finally starting to get warm."],
        ['Hacía tiempo que no teníamos un día así.', "We hadn't had a day like this in a while."],
        ['Vaya día llevo.', 'What a day I’m having.'],
        ['Este fin de semana me viene de maravilla.', 'This weekend comes at a perfect moment for me.'],
      ],
    },
    {
      slug: 'quedar',
      title: 'Making plans',
      nativeTitle: 'Quedar con alguien',
      useWhen: 'The moment a conversation could become a second meeting.',
      tags: ['social'],
      level: 2,
      blocks: [
        ['¿Te apetece que tomemos un café un día de estos?', 'Do you fancy getting a coffee one of these days?', 'apetecer works like gustar, and que + subjunctive follows.'],
        ['¿Cuándo te viene bien?', 'When suits you?'],
        ['Entre semana lo tengo bastante libre.', 'During the week I’m fairly free.'],
        ['¿Quedamos el jueves sobre las siete?', 'Shall we meet Thursday around seven?', 'quedar is the verb for arranging to meet — it has no neat English equivalent.'],
        ['Te mando la dirección por mensaje.', "I'll send you the address by text."],
      ],
    },
    {
      slug: 'problemas',
      title: 'When something goes wrong',
      nativeTitle: 'Cuando algo sale mal',
      useWhen: 'The island you will need under stress, which is when improvising fails.',
      tags: ['transactional', 'problems'],
      level: 2,
      blocks: [
        ['Perdone, creo que hay un problema.', 'Excuse me, I think there is a problem.'],
        ['Pedí esto ayer y no me ha llegado nada.', "I ordered this yesterday and nothing has arrived."],
        ['¿Me podría ayudar, por favor?', 'Could you help me, please?'],
        ['No lo he entendido bien, ¿me lo puede repetir?', "I didn't quite understand — could you repeat it?"],
        ['¿Puede hablar un poco más despacio, por favor?', 'Could you speak a little more slowly, please?'],
      ],
    },
    {
      slug: 'opinion',
      title: 'Opinions and preferences',
      nativeTitle: 'Mi opinión',
      useWhen: 'What turns an interview into a conversation.',
      tags: ['social', 'opinions'],
      level: 3,
      blocks: [
        ['Me parece buena idea.', 'That seems like a good idea to me.', 'parecer works like gustar: *me* parece.'],
        ['No estoy muy convencido, la verdad.', "I'm not very convinced, to be honest."],
        ['Sinceramente, no me gusta demasiado.', "Honestly, I don't like it much."],
        ['Depende.', 'It depends.'],
        ['Estoy bastante de acuerdo contigo.', 'I fairly much agree with you.'],
      ],
    },
  ],

  it: [
    {
      slug: 'abitare',
      title: 'Where I live',
      nativeTitle: 'Dove abito',
      useWhen: 'Pairs with the work island — somebody always asks where you came from.',
      tags: ['introductions', 'place'],
      level: 2,
      blocks: [
        ['Abito in una cittadina del Midwest.', 'I live in a small town in the Midwest.'],
        ['Non c’è granché da fare, ma è proprio questo che mi piace.', "There isn't much to do, but that's exactly what I like."],
        ['Gli inverni sono lunghi e piuttosto freddi.', 'The winters are long and rather cold.'],
        ['Dal centro sono un quarto d’ora in macchina.', "I'm a quarter of an hour from the centre by car."],
        ['Ci sto bene, ma mi manca il mare.', "I'm happy there, but I miss the sea.", 'mancare inverts like French manquer: mi manca il mare.'],
      ],
    },
    {
      slug: 'tempolibero',
      title: 'What I do outside work',
      nativeTitle: 'Il mio tempo libero',
      useWhen: 'The island that keeps a conversation going once the basics are done.',
      tags: ['introductions', 'hobbies'],
      level: 2,
      blocks: [
        ['Nel tempo libero leggo molto e vado a correre.', 'In my free time I read a lot and go running.'],
        ['Il fine settimana di solito faccio una decina di chilometri.', 'At the weekend I usually do about ten kilometres.'],
        ['Inoltre mi interessa molto la cucina.', "Besides that, I'm very interested in cooking."],
        ['Ultimamente sto imparando a cucinare piatti italiani.', "Lately I've been learning to cook Italian dishes."],
        ['Per ora non mi riesce niente di buono.', 'So far nothing turns out well.', 'riuscire takes the person in the dative: non *mi* riesce.'],
      ],
    },
    {
      slug: 'spostarsi',
      title: 'Getting around',
      nativeTitle: 'Spostarsi in città',
      useWhen: 'You need this while tired and carrying luggage. Overlearn it.',
      tags: ['transactional', 'travel'],
      level: 2,
      blocks: [
        ['Scusi, per andare alla stazione?', 'Excuse me, how do I get to the station?'],
        ['Questo autobus va all’aeroporto?', 'Does this bus go to the airport?'],
        ['Un biglietto di andata e ritorno per Firenze, per favore.', 'A return ticket to Florence, please.'],
        ['Da quale binario parte il treno?', 'Which platform does the train leave from?'],
        ['Mi avvisa quando devo scendere?', 'Would you tell me when I have to get off?'],
      ],
    },
    {
      slug: 'spesa',
      title: 'Shopping',
      nativeTitle: 'Fare la spesa',
      useWhen: 'Short, frequent exchanges where hesitating costs you the queue.',
      tags: ['transactional', 'shopping'],
      level: 1,
      blocks: [
        ['Scusi, ce l’ha di un’altra taglia?', 'Excuse me, do you have it in another size?', 'ce l’ha — the ci is idiomatic with avere here and cannot be dropped.'],
        ['Sto solo dando un’occhiata, grazie.', "I'm just having a look, thanks."],
        ['Quanto costa?', 'How much is it?'],
        ['Posso pagare con la carta?', 'Can I pay by card?'],
        ['Prendo questo, grazie.', "I'll take this, thanks."],
      ],
    },
    {
      slug: 'chiacchiere',
      title: 'Weather and small talk',
      nativeTitle: 'Due chiacchiere',
      useWhen: 'The filler that keeps a silence from becoming awkward.',
      tags: ['social'],
      level: 1,
      blocks: [
        ['Bella giornata oggi, vero?', "Lovely day today, isn't it?"],
        ['Finalmente comincia a fare caldo.', "It's finally starting to get warm."],
        ['Era da un po’ che non avevamo una giornata così.', "It had been a while since we had a day like this."],
        ['Che giornata…', 'What a day…'],
        ['Questo fine settimana capita proprio a proposito.', 'This weekend comes at just the right time.'],
      ],
    },
    {
      slug: 'accordi',
      title: 'Making plans',
      nativeTitle: 'Prendere accordi',
      useWhen: 'The moment a conversation could become a second meeting.',
      tags: ['social'],
      level: 2,
      blocks: [
        ['Ti va di prendere un caffè uno di questi giorni?', 'Do you fancy getting a coffee one of these days?', 'Ti va di… is the everyday invitation.'],
        ['Quando ti verrebbe comodo?', 'When would be convenient for you?'],
        ['Durante la settimana sono abbastanza libero.', "During the week I'm fairly free."],
        ['Facciamo giovedì verso le sette?', 'Shall we say Thursday around seven?', 'Facciamo… is how Italians propose a time; it is not "let us do".'],
        ['Ti mando l’indirizzo per messaggio.', "I'll send you the address by message."],
      ],
    },
    {
      slug: 'problemi',
      title: 'When something goes wrong',
      nativeTitle: 'Quando qualcosa va storto',
      useWhen: 'The island you will need under stress, which is when improvising fails.',
      tags: ['transactional', 'problems'],
      level: 2,
      blocks: [
        ['Scusi, credo che ci sia un problema.', 'Excuse me, I think there is a problem.', 'credere che takes the subjunctive: che ci *sia*.'],
        ['L’ho ordinato ieri, ma non è mai arrivato.', "I ordered it yesterday, but it never arrived."],
        ['Mi potrebbe aiutare, per favore?', 'Could you help me, please?'],
        ['Non ho capito bene — me lo può ripetere?', "I didn't quite understand — could you repeat it?"],
        ['Può parlare un po’ più lentamente, per favore?', 'Could you speak a little more slowly, please?'],
      ],
    },
    {
      slug: 'opinione',
      title: 'Opinions and preferences',
      nativeTitle: 'La mia opinione',
      useWhen: 'What turns an interview into a conversation.',
      tags: ['social', 'opinions'],
      level: 3,
      blocks: [
        ['Mi sembra una buona idea.', 'That seems like a good idea to me.'],
        ['Non ne sono così convinto.', "I'm not so convinced of it."],
        ['Sinceramente, non mi piace molto.', "Honestly, I don't like it much."],
        ['Dipende.', 'It depends.'],
        ['Sono abbastanza d’accordo con te.', 'I fairly much agree with you.'],
      ],
    },
  ],
}
